import { Alert, AlertDistribution, BackoffPolicy, RecipientRole, DeliveryMethod } from '../models';
import { Normalized } from '../helpers/norm';
import { enforceEnumValue } from '../helpers/enum';
import { PushClient } from './util/PushClient';
import { User, UsersData } from '../components/modules/usersList/types';
import { CompaniesData, Company } from '../components/modules/companies/types';
import { SitesData, Site, Utility, Customer } from 'src/components/modules/sites/types';

export type AlertDistributionPayload = Normalized<AlertDistribution, 'user', { userId: number }>;

export type AlertPayload = Normalized<
  Alert,
  'relatedAlarmTag' | 'distributionList' | 'isActive',
  { relatedAlarmTagId: string; distributionList: AlertDistributionPayload[] }
>;

export interface AlertUpdate {
  tagId: string;
  backoffPolicy: BackoffPolicy | null;
  distributionList: AlertDistributionPayload[];
}

export const convertDistributionToPayload = ({
  user,
  ...distribution
}: AlertDistribution): AlertDistributionPayload => {
  return {
    ...distribution,
  };
};

export class AdminApi extends PushClient {
  static baseUrl = '/admin';

  public async fetchSiteAlerts(siteId: number): Promise<AlertPayload[]> {
    const alerts = await this.client.get<AlertPayload[]>(
      `${AdminApi.baseUrl}/site/${siteId}/alerts`
    );
    return alerts.map((alertItem) => this.sanitizeAlert(alertItem));
  }

  public async updateAlert(alertUpdate: AlertUpdate): Promise<AlertPayload> {
    const alertItem = await this.client.post<AlertPayload, AlertUpdate>(
      `${AdminApi.baseUrl}/alert`,
      alertUpdate
    );
    return this.sanitizeAlert(alertItem);
  }

  public async terminateGridServices(partnerRefId: number, status: string): Promise<boolean> {
    const body = { partnerRefId, status };
    await this.client.post<boolean, typeof body>(
      AdminApiPathBuilder.TERMINATE_GRID_SERVICES(),
      body
    );
    return true;
  }

  public async switchRateSchedule(siteId: number, rateScheduleId: number): Promise<boolean> {
    const body = { siteId, rateScheduleId };
    const response = await this.client.post<boolean, typeof body>(
      AdminApiPathBuilder.SWITCH_RATESCHEDULE(),
      body
    );
    return response;
  }

  public async getCompanies(page: Number, searchText: string): Promise<CompaniesData> {
    const url = `${AdminApiPathBuilder.GET_COMPANIES()}?page=${page || 1}&search=${searchText}`;
    const companies = await this.client.get<CompaniesData>(url);

    return companies;
  }

  public async editCompany(companyId: number, data: Company): Promise<Company> {
    const url = `${AdminApiPathBuilder.GET_COMPANIES()}/${companyId}`;
    const response = await this.client.put<Company, Company>(url, data);

    return response;
  }

  public async addCompany(data: Company): Promise<Company> {
    const response = await this.client.post<Company, Company>(
      AdminApiPathBuilder.GET_COMPANIES(),
      data
    );

    return response;
  }

  public async getUsers(page: Number, searchText: string): Promise<UsersData> {
    const url = `${AdminApiPathBuilder.GET_USERS()}?page=${page || 1}&search=${searchText}`;
    const users = await this.client.get<UsersData>(url);

    return users;
  }

  public async editUser(userId: number, data: User): Promise<User> {
    const url = `${AdminApiPathBuilder.GET_USERS()}/${userId}`;
    const response = await this.client.put<User, User>(url, data);

    return response;
  }

  public async getSites(page: number, searchText: string): Promise<SitesData> {
    const url = `${AdminApiPathBuilder.GET_SITES()}?page=${page || 1}&search=${searchText}`;
    const sites = await this.client.get<SitesData>(url);

    return sites;
  }

  public async editSite(siteId: number, data: Site): Promise<Site> {
    const url = `${AdminApiPathBuilder.GET_SITES()}/${siteId}`;
    const response = await this.client.put<Site, Site>(url, data);

    return response;
  }

  public async getUtilitiesList(): Promise<Utility[]> {
    const url = `/list/utilities`;
    const response = await this.client.get<Utility[]>(url);

    return response;
  }

  public async getCustomersList(): Promise<Customer[]> {
    const url = `/list/customers`;
    const response = await this.client.get<Customer[]>(url);

    return response;
  }

  private sanitizeAlert(apiAlert: AlertPayload): AlertPayload {
    return {
      ...apiAlert,
      backoffPolicy:
        apiAlert.backoffPolicy && enforceEnumValue(BackoffPolicy, apiAlert.backoffPolicy),
      distributionList: apiAlert.distributionList.map((dist) => ({
        ...dist,
        recipientRole: enforceEnumValue(RecipientRole, dist.recipientRole),
        deliveryMethod: enforceEnumValue(DeliveryMethod, dist.deliveryMethod),
      })),
    };
  }
}

export class AdminApiPathBuilder {
  static TERMINATE_GRID_SERVICES = () => `${AdminApi.baseUrl}/terminategridservices`;

  static SWITCH_RATESCHEDULE = () => `${AdminApi.baseUrl}/switchrateschedule`;

  static GET_COMPANIES = () => `${AdminApi.baseUrl}/companies`;

  static GET_USERS = () => `${AdminApi.baseUrl}/users`;

  static GET_SITES = () => `${AdminApi.baseUrl}/sites`;
}
